import { useContext } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import LegacyButton from '@atlaskit/button';
import Button, { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Flex } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';

import {
  FeedbackCollector,
  FEEDBACK_CONTEXT_LABELS,
} from 'src/components/pipelines/components/feedback-collector';
import { DropdownMenuMockContext } from 'src/components/pipelines/contexts/DropdownMenuMockContext';
import { getCapabilities } from 'src/components/pipelines/redux/selectors/pipelines';
import { useAnalytics } from 'src/components/settings/analytics';
import { useRepositoryAdminStatus } from 'src/hooks/use-repository-admin-status';

import { messages } from '../merge-queues.i18n';
import { useIsEmpty, useMergeQueuesActions } from '../provider';
import {
  MergeQueuePullRequestActionsProps,
  MergeQueuesInlineSettingsProps,
  MergeQueueState,
} from '../types';

const learnMoreLink =
  'https://support.atlassian.com/bitbucket-cloud/docs/manage-pull-requests-with-merge-queues/';

export const HowToUseMergeQueuesLink = () => {
  const { formatMessage } = useIntl();

  return (
    <LegacyButton
      href={learnMoreLink}
      target="_blank"
      testId={messages.learnMore.id}
      appearance="link"
      spacing="none"
    >
      {formatMessage(messages.learnMore)}
    </LegacyButton>
  );
};

export const ViewPullRequestsButton = ({
  onViewClick,
}: Pick<MergeQueuesInlineSettingsProps, 'onViewClick'>) => {
  const { formatMessage } = useIntl();

  return (
    <IconButton
      icon={PullRequestIcon}
      isTooltipDisabled={false}
      label={formatMessage(messages.viewPullRequestsAction)}
      onClick={onViewClick}
      testId="view-pull-requests-button"
    />
  );
};

export const InlineSettings = ({
  mergeQueue,
  pullRequestsCount,
  onClearClick,
  onDeleteClick,
  onEditClick,
  onStateUpdate,
  onViewClick,
}: MergeQueuesInlineSettingsProps) => {
  const { formatMessage } = useIntl();
  const { defaultOpen } = useContext(DropdownMenuMockContext);

  return (
    <DropdownMenu<HTMLButtonElement>
      defaultOpen={defaultOpen}
      placement="bottom-end"
      trigger={({ triggerRef, ...props }) => (
        <IconButton
          {...props}
          icon={ShowMoreHorizontalIcon}
          label={formatMessage(messages.actionsTableHeader)}
          ref={triggerRef}
          testId="merge-queues-action-button"
        />
      )}
      shouldRenderToParent
    >
      <DropdownItemGroup>
        {onViewClick && (
          <DropdownItem onClick={onViewClick}>
            {formatMessage(messages.viewPullRequestsAction)}
          </DropdownItem>
        )}
        {onEditClick && (
          <DropdownItem onClick={onEditClick}>
            {formatMessage(messages.editAction)}
          </DropdownItem>
        )}
        {onStateUpdate && (
          <Tooltip
            canAppear={() => mergeQueue.isActive}
            position="left"
            content={formatMessage(messages.activateActionDisabledTooltip)}
          >
            <DropdownItem
              onClick={() => onStateUpdate(MergeQueueState.ACTIVE)}
              isDisabled={mergeQueue.isActive}
            >
              {formatMessage(messages.activateAction)}
            </DropdownItem>
          </Tooltip>
        )}
        {onStateUpdate && (
          <Tooltip
            canAppear={() => mergeQueue.isInactive}
            position="left"
            content={formatMessage(messages.deactivateActionDisabledTooltip)}
          >
            <DropdownItem
              onClick={() => onStateUpdate(MergeQueueState.INACTIVE)}
              isDisabled={mergeQueue.isInactive}
            >
              {formatMessage(messages.deactivateAction)}
            </DropdownItem>
          </Tooltip>
        )}
        {onStateUpdate && (
          <Tooltip
            canAppear={() => mergeQueue.isDraining}
            position="left"
            content={formatMessage(messages.drainActionDisabledTooltip)}
          >
            <DropdownItem
              onClick={() => onStateUpdate(MergeQueueState.DRAINING)}
              isDisabled={mergeQueue.isDraining}
            >
              {formatMessage(messages.drainAction)}
            </DropdownItem>
          </Tooltip>
        )}
        {onClearClick && (
          <Tooltip
            canAppear={() => mergeQueue.isActive}
            position="left"
            content={formatMessage(messages.clearActionDisabledTooltip)}
          >
            <DropdownItem
              onClick={onClearClick}
              isDisabled={mergeQueue.isActive}
            >
              {formatMessage(messages.clearAction)}
            </DropdownItem>
          </Tooltip>
        )}
        {onDeleteClick && (
          <Tooltip
            canAppear={() => mergeQueue.isActive || pullRequestsCount > 0}
            position="left"
            content={formatMessage(messages.deleteActionDisabledTooltip)}
          >
            <DropdownItem
              onClick={onDeleteClick}
              isDisabled={mergeQueue.isActive || pullRequestsCount > 0}
            >
              {formatMessage(messages.deleteAction)}
            </DropdownItem>
          </Tooltip>
        )}
      </DropdownItemGroup>
    </DropdownMenu>
  );
};

export const MergeQueuePullRequestActions = ({
  onDequeueClick,
  onMoveToTopClick,
}: MergeQueuePullRequestActionsProps) => {
  const { formatMessage } = useIntl();
  const { defaultOpen } = useContext(DropdownMenuMockContext);

  return (
    <DropdownMenu<HTMLButtonElement>
      defaultOpen={defaultOpen}
      placement="auto"
      trigger={({ triggerRef, ...props }) => (
        <IconButton
          {...props}
          icon={ShowMoreHorizontalIcon}
          label={formatMessage(messages.actionsTableHeader)}
          appearance="subtle"
          ref={triggerRef}
          testId="merge-queue-pull-request-action-button"
        />
      )}
      shouldRenderToParent
    >
      <DropdownItemGroup>
        {onDequeueClick && (
          <DropdownItem onClick={onDequeueClick}>
            {formatMessage(messages.removeFromQueueAction)}
          </DropdownItem>
        )}
        {onMoveToTopClick && (
          <DropdownItem onClick={onMoveToTopClick}>
            {formatMessage(messages.moveToTopAction)}
          </DropdownItem>
        )}
      </DropdownItemGroup>
    </DropdownMenu>
  );
};

export const CreateMergeQueuesButton = ({
  isCentralButton = false,
}: {
  isCentralButton?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const { publishButtonClickEvent } = useAnalytics({
    source: 'MergeQueuesSettingsPage',
  });
  const [{ isEmpty }] = useIsEmpty();
  const [, { setIsCreateModalOpen, setIsEditModalOpen }] =
    useMergeQueuesActions();
  const isAdmin = useRepositoryAdminStatus();
  const capabilities = useSelector(getCapabilities);

  const isPaidOrPremium =
    capabilities.hasFetchedCapabilities && capabilities.isPaidOrPremium;
  const isPipelinesEnabled =
    capabilities.hasFetchedCapabilities && capabilities.pipelinesEnabled;

  const onCreateMergeQueueClick = () => {
    setIsEditModalOpen(false);
    setIsCreateModalOpen(true);
    publishButtonClickEvent('createMergeQueue', {
      emptyState: isEmpty,
    });
  };

  if (
    (isEmpty && !isCentralButton) ||
    !isAdmin ||
    !isPaidOrPremium ||
    !isPipelinesEnabled
  ) {
    return null;
  }

  return (
    <Button
      testId={messages.createMergeQueue.id}
      appearance="primary"
      onClick={onCreateMergeQueueClick}
    >
      {formatMessage(messages.createMergeQueue)}
    </Button>
  );
};

export const MergeQueuesSettingsHeaderActions = () => {
  return (
    <Flex alignItems="center" gap="space.150">
      <FeedbackCollector
        feedbackContextLabel={FEEDBACK_CONTEXT_LABELS.MERGE_QUEUES_SETTINGS}
      />
      <CreateMergeQueuesButton />
    </Flex>
  );
};
